import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../stores/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSetMenu } from "../../../hooks/useSetMenu";
import FormSelect from '../../Generic/FormSelect';
import * as yup from "yup";
import {
    Alert, CircularProgress, Card, CardContent, Typography,
    Chip, Box, Stepper, Step, StepLabel, TextField, Button
} from '@mui/material';
import {
    Row, Col, Form, Accordion
} from 'react-bootstrap';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ListAltIcon from '@mui/icons-material/ListAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const SubmitCriteria = () => {
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [createdCriteria, setCreatedCriteria] = useState(null);

    // Criteria values state
    const [batchValues, setBatchValues] = useState("");
    const [parsedValues, setParsedValues] = useState([]);
    const [addingValues, setAddingValues] = useState(false);

    useSetMenu(mainStore.UserSubmissionMenuType, mainStore.SubmitCriteriaMenuNo);

    // Predefined weight coefficients like CriteriaManager
    const weightCoefficients = [
        { value: "1.0", label: "W = 1.00 (Mandatory)" },
        { value: "0.5", label: "W = 0.50 (Important)" },
        { value: "0.33", label: "W = 0.33 (Nice to have)" }
    ];

    const onError = (errors, e) =>
        enqueueSnackbar(t("onErrorSubmit"), { variant: "error" });

    const schema = yup.object({
        name: yup
            .string()
            .required(t('requiredField'))
            .max(100, t('maxLength', { max: 100 })),
        panelId: yup.string().required(t("selectRequired")),
        categoryId: yup.string().required(t("selectRequired")),
        subcategoryId: yup.string().required(t("selectRequired")),
        weightCoefficient: yup.string().required(t("selectRequired")),
    });

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            weightCoefficient: "0.5",
            isMandatory: false
        }
    });

    const isMandatory = watch("isMandatory");
    const weightCoefficient = watch("weightCoefficient");

    // Auto-set weight coefficient based on isMandatory
    useEffect(() => {
        if (isMandatory) {
            setValue("weightCoefficient", "1.0");
        }
    }, [isMandatory, setValue]);

    // Auto-set isMandatory based on weight coefficient
    useEffect(() => {
        if (weightCoefficient === "1.0") {
            setValue("isMandatory", true);
        } else {
            setValue("isMandatory", false);
        }
    }, [weightCoefficient, setValue]);

    // Process batch values into array
    useEffect(() => {
        if (batchValues.trim()) {
            const values = batchValues
                .split(/[\n,;]/)
                .map(val => val.trim())
                .filter(val => val.length > 0 && val.length <= 250);
            setParsedValues(values);
        } else {
            setParsedValues([]);
        }
    }, [batchValues]);

    const onSubmit = async (data) => {
        setSubmitting(true);
        try {
            const payload = {
                name: data.name,
                subcategoryId: parseInt(data.subcategoryId),
                categoryId: parseInt(data.categoryId),
                panelId: parseInt(data.panelId),
                weightCoefficient: parseFloat(data.weightCoefficient),
                isMandatory: data.isMandatory || false
            };

            const response = await axiosPrivate.post("/Criterias/Submit", payload);

            setCreatedCriteria(response.data);
            enqueueSnackbar(t("submitCriteriaSuccess"), { variant: "success" });
            setActiveStep(1); // Move to values step
        } catch (error) {
            console.error("Error submitting criteria:", error);
            enqueueSnackbar(t("submitCriteriaError"), { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddValues = async () => {
        if (!createdCriteria || parsedValues.length === 0) {
            enqueueSnackbar(t("pleaseAddAtLeastOneValue"), { variant: "warning" });
            return;
        }

        setAddingValues(true);
        try {
            const batchRequest = {
                criteriaId: createdCriteria.id,
                panelId: createdCriteria.panelId,
                categoryId: createdCriteria.categoryId,
                subcategoryId: createdCriteria.subcategoryId,
                values: parsedValues
            };

            await axiosPrivate.post("/CriteriaValues/Submit", batchRequest);
            enqueueSnackbar(t("valuesAddedSuccess"), { variant: "success" });
            setSubmitted(true);
        } catch (error) {
            console.error("Error adding criteria values:", error);
            enqueueSnackbar(t("valuesAddedError"), { variant: "error" });
        } finally {
            setAddingValues(false);
        }
    };

    const handleSkipValues = () => {
        setSubmitted(true);
    };

    const onCancel = () => navigate(-1);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [t]);

    //#region Dropdowns
    const [panels, setPanels] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [selectedPanel, setSelectedPanel] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    async function loadData() {
        try {
            const panelsResponse = await axiosPrivate.get("/Panels/SelectOptions");
            setPanels(panelsResponse.data);
        } catch (err) {
            enqueueSnackbar(t("loadDataError"), { variant: "error" });
        }
    }

    const handleSubcategoryChange = selectedOption => {
        setValue("subcategoryId", selectedOption?.value, { shouldValidate: true });
        setSelectedSubcategory(selectedOption);
    };

    const handleCategoryChange = async (selectedOption) => {
        setValue("categoryId", selectedOption?.value, { shouldValidate: true });
        setSelectedCategory(selectedOption);

        if (!selectedOption) {
            setSubcategories([]);
            setSelectedSubcategory(null);
            setValue("subcategoryId", null, { shouldValidate: true });
            return;
        }

        try {
            const response = await axiosPrivate.get(`/Subcategories/SelectOptionsByCategory/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setSubcategories([]);
            } else {
                setSubcategories(response.data);
            }
            setSelectedSubcategory(null);
            setValue("subcategoryId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingSubcategories"), { variant: "error" });
        }
    };

    const handlePanelChange = async (selectedOption) => {
        setValue("panelId", selectedOption?.value, { shouldValidate: true });
        setSelectedPanel(selectedOption);

        if (!selectedOption) {
            setCategories([]);
            setSubcategories([]);
            setSelectedCategory(null);
            setSelectedSubcategory(null);
            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });
            return;
        }

        try {
            const response = await axiosPrivate.get(`/Categories/SelectOptionsByPanel/${selectedOption.value}`);
            if (typeof response.data === "string" && response.data === "No records") {
                setCategories([]);
            } else {
                setCategories(response.data);
            }
            setSelectedCategory(null);
            setSubcategories([]);
            setSelectedSubcategory(null);
            setValue("categoryId", null, { shouldValidate: true });
            setValue("subcategoryId", null, { shouldValidate: true });
        } catch (error) {
            enqueueSnackbar(t("errorLoadingCategories"), { variant: "error" });
        }
    };

    const handleWeightCoefficientChange = selectedOption => {
        setValue("weightCoefficient", selectedOption?.value, { shouldValidate: true });
    };
    //#endregion

    // Show success message after submission
    if (submitted) {
        return (
            <div className="container mt-4">
                <Alert severity="success" className="mb-4">
                    <h5>{t("submitCriteriaSuccessTitle")}</h5>
                    <p>{t("submitCriteriaSuccessMessage")}</p>
                </Alert>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/MySubmissions")}
                    >
                        {t("viewMySubmissions")}
                    </button>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setSubmitted(false);
                            setActiveStep(0);
                            setCreatedCriteria(null);
                            setSelectedPanel(null);
                            setSelectedCategory(null);
                            setSelectedSubcategory(null);
                            setCategories([]);
                            setSubcategories([]);
                            setBatchValues("");
                            setParsedValues([]);
                        }}
                    >
                        {t("submitAnother")}
                    </button>
                </div>
            </div>
        );
    }

    const steps = [t('criteriaDetails'), t('criteriaValues')];

    return (
        <div className="container">
            <Alert severity="info" className="mb-4" icon={<InfoOutlinedIcon />}>
                {t("submitCriteriaInfo")}
            </Alert>

            {/* Stepper */}
            <Stepper activeStep={activeStep} className="mb-4">
                {steps.map((label, index) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Step 1: Criteria Details */}
            {activeStep === 0 && (
                <Card>
                    <CardContent>
                        <Typography variant="h6" className="mb-3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AddCircleOutlineIcon color="primary" />
                            {t("submitCriteriaTitle")}
                        </Typography>

                        <Form onSubmit={handleSubmit(onSubmit, onError)}>
                            {/* Criteria Name */}
                            <Row className="mb-3">
                                <Col md={12}>
                                    <Form.Group>
                                        <Form.Label>{t("criteriaName")} *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder={t("enterCriteriaName")}
                                            isInvalid={!!errors.name}
                                            {...register("name")}
                                        />
                                        {errors.name && (
                                            <Form.Control.Feedback type="invalid">
                                                {errors.name.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Panel, Category, Subcategory */}
                            <Row className="mb-3">
                                <FormSelect
                                    label="panel"
                                    name="panelId"
                                    register={register}
                                    errors={errors}
                                    colSize={4}
                                    options={panels}
                                    onChange={handlePanelChange}
                                    value={selectedPanel}
                                    isClearable={true}
                                />
                                <FormSelect
                                    label="category"
                                    name="categoryId"
                                    value={selectedCategory}
                                    register={register}
                                    errors={errors}
                                    colSize={4}
                                    options={categories}
                                    onChange={handleCategoryChange}
                                    isClearable={true}
                                />
                                <FormSelect
                                    label="subcategory"
                                    name="subcategoryId"
                                    value={selectedSubcategory}
                                    register={register}
                                    errors={errors}
                                    colSize={4}
                                    options={subcategories}
                                    onChange={handleSubcategoryChange}
                                    isClearable={true}
                                />
                            </Row>

                            {/* Weight Coefficient and IsMandatory */}
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>{t("weightCoefficient")} *</Form.Label>
                                        <Form.Select
                                            isInvalid={!!errors.weightCoefficient}
                                            {...register("weightCoefficient")}
                                            onChange={(e) => {
                                                const selected = weightCoefficients.find(wc => wc.value === e.target.value);
                                                handleWeightCoefficientChange(selected);
                                            }}
                                            value={weightCoefficient}
                                        >
                                            <option value="">{t("selectRequired")}</option>
                                            {weightCoefficients.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        {errors.weightCoefficient && (
                                            <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                                {errors.weightCoefficient.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>{t("isMandatory")}</Form.Label>
                                        <div style={{
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            padding: '10px 14px',
                                            marginTop: '4px'
                                        }}>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="isMandatory"
                                                    style={{ width: '18px', height: '18px' }}
                                                    {...register("isMandatory")}
                                                    disabled={weightCoefficient === "1.0"}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="isMandatory"
                                                    style={{ marginLeft: '8px', fontWeight: 500, color: '#374151' }}
                                                >
                                                    {t("yes")} - {t("mandatoryCriteria")}
                                                </label>
                                            </div>
                                        </div>
                                        <Form.Text className="text-muted">
                                            {t("weightCoefficientHint")}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Buttons */}
                            <div className="d-flex justify-content-between mt-4">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={onCancel}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <>
                                            <AddCircleOutlineIcon style={{ fontSize: 18, marginRight: 6 }} />
                                            {t("submitForReview")}
                                        </>
                                    )}
                                </button>
                            </div>
                        </Form>
                    </CardContent>
                </Card>
            )}

            {/* Step 2: Add Criteria Values */}
            {activeStep === 1 && createdCriteria && (
                <Card>
                    <CardContent>
                        <Alert severity="success" className="mb-3">
                            <CheckCircleIcon style={{ marginRight: 8 }} />
                            {t("criteriaCreatedNowAddValues")}
                        </Alert>

                        <Typography variant="h6" className="mb-3" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ListAltIcon color="primary" />
                            {t("addValuesTitle")} - {createdCriteria.name}
                        </Typography>

                        {/* Criteria Info Card */}
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>{t("panel")}:</strong> {createdCriteria.panelName}
                                {" | "}
                                <strong>{t("category")}:</strong> {createdCriteria.categoryName}
                                {" | "}
                                <strong>{t("subcategory")}:</strong> {createdCriteria.subcategoryName}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Chip
                                    size="small"
                                    label={`W = ${createdCriteria.weightCoefficient?.toFixed(2)}`}
                                    color={createdCriteria.weightCoefficient === 1 ? "error" : createdCriteria.weightCoefficient === 0.5 ? "warning" : "success"}
                                    sx={{ mr: 1 }}
                                />
                                {createdCriteria.isMandatory && (
                                    <Chip size="small" label={t("mandatory")} color="primary" />
                                )}
                            </Box>
                        </Box>

                        {/* Batch Values Input */}
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontWeight: 500 }}>
                                {t("batchValues")}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={batchValues}
                                onChange={(e) => setBatchValues(e.target.value)}
                                placeholder={t("enterValuesSeperatedBy")}
                                style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px'
                                }}
                            />
                            <Form.Text className="text-muted">
                                {t("batchValuesHelp")}
                            </Form.Text>
                        </Form.Group>

                        {/* Parsed Values Preview */}
                        {parsedValues.length > 0 && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
                                <Typography variant="body2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {t("valuePreview")}
                                    <Chip size="small" label={parsedValues.length} color="primary" />
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                    {parsedValues.map((val, index) => (
                                        <Chip
                                            key={index}
                                            label={val}
                                            size="small"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Buttons */}
                        <div className="d-flex justify-content-between mt-4">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleSkipValues}
                            >
                                {t("skipAndGoToList")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddValues}
                                disabled={addingValues || parsedValues.length === 0}
                            >
                                {addingValues ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <>
                                        <AddCircleOutlineIcon style={{ fontSize: 18, marginRight: 6 }} />
                                        {t("saveValuesAndGoToList")}
                                    </>
                                )}
                            </button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default SubmitCriteria;
